import React from 'react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { 
  CheckCircle2, 
  AlertCircle,
  ExternalLink,
  Building2,
  Users,
  MapPin,
  ClipboardList,
  Euro
} from 'lucide-react';

const InhouseKursePage = () => {
  // Vorteile auf einen Blick
  const vorteile = [
    'Inhouse-Schulungen direkt im Betrieb',
    'Schulungen auf aktuellstem Stand der DGUV-Vorgaben',
    'Mindestteilnehmerzahl: 10 Personen',
    'Keine Fahrtkosten',
    'Direkte Abrechnung mit der Berufsgenossenschaft',
    'Flexibel, mobil und zeitnah verfügbar'
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <SEO 
        page="inhouse"
        title="Inhouse-Kurse für Betriebe | Erste Hilfe Bottrop"
        description="Inhouse-Schulungen für Betriebe in NRW. Erste-Hilfe-Kurse direkt vor Ort – mobil, professionell & BG-konform. Kostenübernahme durch Berufsgenossenschaft."
      />
      <Navbar />
      
      {/* MOBILE: Klassischer Hero wie Dortmund (Bild oben, Titel darunter) */}
      <div className="md:hidden">
        {/* Vollbreites Bild */}
        <div className="w-full">
          <img 
            src="https://customer-assets.emergentagent.com/job_5307dd81-655c-49d9-9c3b-c78ff352348d/artifacts/sail061u_Hero%20Inhouse.jpeg"
            alt="Inhouse-Schulungen für Betriebe"
            className="w-full h-auto object-cover"
            style={{ aspectRatio: '16/9' }}
          />
        </div>
        
        {/* Titel-Bereich unter dem Bild */}
        <div className="bg-white px-4 py-6">
          <h1 className="text-2xl font-bold text-[#1e3a5f] leading-tight mb-3" data-testid="page-title-mobile">
            Inhouse-Schulungen<br />
            <span className="text-xl">bei Ihnen im Betrieb</span>
          </h1>
          <p className="text-slate-600 text-base leading-relaxed">
            Erste-Hilfe-Kurse direkt vor Ort – mobil, professionell & BG-konform.
          </p>
        </div>
      </div>

      {/* DESKTOP: Bestehender Hero mit Gradient-Overlay (unverändert) */}
      <div className="hidden md:block">
        <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div 
            className="relative overflow-hidden rounded-xl"
            style={{ 
              backgroundImage: `url("https://customer-assets.emergentagent.com/job_5307dd81-655c-49d9-9c3b-c78ff352348d/artifacts/sail061u_Hero%20Inhouse.jpeg")`,
              backgroundSize: '100% 100%',
              backgroundPosition: 'center center'
            }}
          >
            {/* Gradient Overlay - NUR links für Textbereich */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 via-[#1e3a5f]/70 to-[#1e3a5f]/20" />
            
            {/* Content - Einheitliche Höhe */}
            <div className="relative px-8 lg:px-10 py-12 lg:py-14">
              <div className="flex flex-row items-center justify-between gap-6 min-h-[240px]">
                {/* Titel + Zusatzinfo - Links */}
                <div className="max-w-xl">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-full text-sm font-semibold shadow-sm">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      BG-konform
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 text-[#1e3a5f] rounded-full text-sm font-semibold shadow-sm">
                      Bundesweit
                    </span>
                  </div>
                  
                  <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight drop-shadow-sm" data-testid="page-title">
                    Inhouse-Schulungen bei Ihnen im Betrieb
                  </h1>
                  
                  <p className="text-white/90 text-lg mt-3 drop-shadow-sm">
                    Erste-Hilfe-Kurse direkt vor Ort – mobil, professionell & BG-konform.
                  </p>
                  
                  {/* Zusätzlicher Teaser-Text */}
                  <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                    <p className="text-white font-semibold text-lg">
                      Wir kommen zu Ihnen – Bundesweit
                    </p>
                    <p className="text-white/80 text-sm mt-1">
                      Alles, was Sie brauchen: einen Raum und Stühle. Den Rest bringen wir mit.
                    </p>
                  </div>
                </div>
                
                {/* CTA Button - Rechts, vertikal zentriert */}
                <div className="flex-shrink-0 flex items-center">
                  <Button 
                    size="lg"
                    className="bg-white text-[#1e3a5f] hover:bg-slate-100 font-semibold px-8 py-6 text-base shadow-xl"
                    onClick={() => window.location.href = '/kontakt'}
                  >
                    Jetzt anfragen
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 py-8 sm:py-12">
        <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          {/* Gesetzliche Pflicht */}
          <Card className="border-0 shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#1e3a5f]/10 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-[#1e3a5f]" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-[#1e3a5f] mb-2">
                    Erste Hilfe im Betrieb – gesetzliche Pflicht
                  </h2>
                  <p className="text-slate-600">
                    Erste-Hilfe-Kurse für Betriebe sind gemäß <strong>DGUV Vorschrift 1 § 26</strong> verpflichtend.
                    Alle Firmen, Vereine und Institutionen, die bei einer Berufsgenossenschaft oder einer Landesunfallkasse 
                    versichert sind, müssen ausgebildete betriebliche Ersthelfer benennen.
                  </p>
                  <a
                    href="https://www.dguv.de/fb-erstehilfe/mitglied/kostenuebernahme/index.jsp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[#1e3a5f] hover:underline mt-3"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Mehr zur DGUV Vorschrift 1
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ihr Partner */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                <Users className="w-5 h-5" />
                Ihr Partner für Inhouse-Kurse im Betrieb
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-600">
                Wir verstehen uns nicht nur als notwendiges Übel, sondern als <strong>verlässlicher Partner</strong> für 
                betriebliche Erste-Hilfe-Schulungen. Unsere Kurse sind <strong>praxisnah</strong>, <strong>aktuell</strong> und 
                <strong> professionell</strong> durchgeführt.
              </p>
            </CardContent>
          </Card>

          {/* Inhouse-Kurse vor Ort */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                <MapPin className="w-5 h-5" />
                Inhouse-Kurse direkt bei Ihnen vor Ort
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-600">
                Dank unserer mobilen Struktur bieten wir <strong>Inhouse-Schulungen direkt in Ihrem Betrieb</strong> an – 
                Bundesweit.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="font-medium text-blue-800 mb-3">Sie stellen lediglich:</p>
                <ul className="space-y-2 text-blue-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    einen geeigneten Raum
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Stühle für die Teilnehmenden
                  </li>
                </ul>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="font-medium text-green-800 mb-3">Alles Weitere bringen wir mit:</p>
                <ul className="space-y-2 text-green-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Beamer
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Schulungsmaterial
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Übungspuppen
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Komplette technische Ausstattung
                  </li>
                </ul>
                <p className="text-green-600 text-sm mt-3 italic">
                  (Gute Laune, Interesse an Erster Hilfe – und ein Kaffee sind natürlich gern gesehen 😉)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Kosten für Betriebe */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                <Euro className="w-5 h-5" />
                Kosten für Betriebe
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-600">
                In den meisten Fällen entstehen für Betriebe <strong>keine Kosten</strong>:
              </p>
              
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Die Kurskosten werden in der Regel von der zuständigen <strong>Berufsgenossenschaft</strong> übernommen</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Keine Fahrtkosten</strong>, keine Zusatzkosten</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Abrechnung erfolgt <strong>direkt über uns</strong> mit der Berufsgenossenschaft</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Mindestteilnehmerzahl */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                <Users className="w-5 h-5" />
                Mindestteilnehmerzahl
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-600">
                Die <strong>Mindestteilnehmerzahl für Inhouse-Kurse beträgt 10 Personen</strong>.
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="mb-3">
                      Sollte ein Betrieb weniger Teilnehmende haben (z. B. 5–9 Personen), gibt es zwei Möglichkeiten:
                    </p>
                    <ul className="space-y-2 list-disc list-inside mb-3">
                      <li>Die Teilnehmenden können alternativ an einem Kurs in unseren Räumlichkeiten in Bottrop teilnehmen.</li>
                      <li>Wenn der Kurs trotz geringerer Teilnehmerzahl im Betrieb stattfinden soll, rechnen wir die vorhandenen Teilnehmenden (z. B. 6, 7, 8 oder 9 Personen) regulär mit der Berufsgenossenschaft ab.</li>
                    </ul>
                    <p className="mb-3">
                      Die fehlenden Teilnehmenden bis zur Mindestanzahl von 10 Personen werden dem Betrieb separat in Rechnung gestellt, zum regulären Preis eines Ersthelferkurses (<strong>35 € pro Person</strong>).
                    </p>
                    <p className="font-medium">
                      Beispiel:<br />
                      Bei 6 Teilnehmenden werden 6 Personen mit der Berufsgenossenschaft abgerechnet, die fehlenden 4 Personen werden dem Betrieb mit 4 × 35 € in Rechnung gestellt.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Besonderheiten bei Pflege */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                <Building2 className="w-5 h-5" />
                Besonderheiten bei Pflege & medizinischem Personal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-purple-800">
                  Bei <strong>Pflegeeinrichtungen</strong> oder Betrieben mit <strong>medizinischem Fachpersonal</strong> kann es 
                  vorkommen, dass die Kosten nicht von der BG übernommen werden. In diesen Fällen erfolgt die 
                  <strong> Abrechnung direkt mit dem Betrieb</strong>.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Vorteile auf einen Blick */}
          <Card className="border-0 shadow-md bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] text-white">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-white">
                <ClipboardList className="w-5 h-5" />
                Unsere Vorteile auf einen Blick
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {vorteile.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-white/90">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* CTA zur Kontaktaufnahme */}
          <Card className="border-0 shadow-md">
            <CardContent className="py-8 text-center">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">
                Interesse an einem Inhouse-Kurs?
              </h3>
              <p className="text-slate-600 mb-4">
                Kontaktieren Sie uns für ein unverbindliches Angebot – wir melden uns zeitnah bei Ihnen.
              </p>
              <a href="/kontakt" data-testid="cta-kontakt">
                <Button className="bg-green-600 hover:bg-green-700 text-white px-8 py-3">
                  Jetzt Kontakt aufnehmen
                </Button>
              </a>
            </CardContent>
          </Card>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default InhouseKursePage;
